import context from '../../Context'
import moment from 'moment'
import UIUtils from './UIUtils'
import { toFixed } from 'bootstrap-vue/esm/utils/number'
import { WELL_STATUS } from '../factory/DataModel'
import Theme from './Theme'
import { SCORE_THRESHOLDS } from '../const'
import Vue from 'vue'

const validateEmail = email => {
    return email.match(
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
}

export default {
    validateEmail,
    getStatusColor(status, include_none) {
        const { FROZEN, TRANSFERRED, THAWED, PENDING, DISCARDED } = WELL_STATUS
        const { blue, green, purple, yellow, red, gray } = Theme.statusColors

        const colorsByStatus = {
            [PENDING]: yellow,
            [FROZEN]: blue,
            [THAWED]: purple,
            [TRANSFERRED]: green,
            [DISCARDED]: red
        }

        return colorsByStatus[status] || (include_none ? gray : '')
    },
    getColoredRangeMetricColor(coloredRangeMetric) {
        const { delta, range } = coloredRangeMetric
        const rangeLength = range.length

        for (let i = rangeLength - 2; i >= 0; i--) {
            const colorRange = range[i]

            if (delta > colorRange.time) {
                return range[i + 1].color
            }
        }

        if (delta < range[0].time) {
            return range[0].color
        } else {
            return range[rangeLength - 1].color
        }
    },
    getColoredRangeMetricColorClass(coloredRangeMetric) {
        const color = this.getColoredRangeMetricColor(coloredRangeMetric)

        return this.getEventRangeColor(color)
    },
    getEventRangeColor(rangeColor, text = false) {
        const type = text ? 'text' : 'background'

        switch (rangeColor) {
            case 'green':
                return Theme.ragClasses[type].green

            case 'amber':
                return Theme.ragClasses[type].amber

            case 'red':
                return Theme.ragClasses[type].red
        }
    },
    getBlastScoreColor(tenant, score, colorBackground, onlyBackgroundGreen, hexColors) {
        const { blastScoreLow, blastScoreHigh } = SCORE_THRESHOLDS

        return getScoreColor(
            tenant,
            score,
            blastScoreLow,
            blastScoreHigh,
            colorBackground,
            onlyBackgroundGreen,
            hexColors
        )
    },
    getKidScoreColor(tenant, score, colorBackground, onlyBackgroundGreen, hexColors) {
        const { kidScoreLow, kidScoreHigh } = SCORE_THRESHOLDS

        return getScoreColor(
            tenant,
            score,
            kidScoreLow,
            kidScoreHigh,
            colorBackground,
            onlyBackgroundGreen,
            hexColors
        )
    },
    showModal(id, onShow, okCallback, cancelCallback, focusRef) {
        return context.getModalPanels().show(id, onShow, okCallback, cancelCallback, focusRef)
    },
    closeModal(id) {
        return context.getModalPanels().cancel(null, false, id)
    },
    isModalOpen(id = null) {
        return context.getModalPanels().isOpen(id)
    },
    toast(message, variant = 'info', dismiss = 3000, toaster = 'b-toaster-top-center') {
        context.App.$bvToast.toast(message, {
            noCloseButton: true,
            variant,
            toaster,
            autoHideDelay: dismiss,
            solid: false
        })
    },
    showLoadingMask() {
        Vue.$store.commit('common/showLoader', true)
    },
    hideLoadingMask() {
        Vue.$store.commit('common/showLoader', false)
    },
    convertHex(hex, opacity) {
        hex = hex.replace('#', '')
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)

        const result = 'rgba(' + r + ',' + g + ',' + b + ',' + opacity + ')'

        return result
    },
    getCount(str, phrase) {
        return (str.match(new RegExp(phrase, 'g')) || []).length
    },
    setOpacity(color, opacity) {
        if (color.indexOf('#') >= 0) {
            return this.convertHex(color, opacity)
        } else {
            if (this.getCount(color, ',') === 3) {
                return color.substring(0, color.lastIndexOf(',')) + ', ' + opacity + ')'
            } else {
                return color.replace('rgb(', 'rgba(').replace(')', ', ' + opacity + ')')
            }
        }
    },
    formatDate(date, fromNow, withTime = false, format = null) {
        if (!date) return null

        const momentDate = moment(date)

        if (!momentDate.isValid()) return date

        if (fromNow) {
            return momentDate.fromNow()
        } else {
            return momentDate.format(format || (withTime ? 'MMM DD YYYY HH:mm' : 'MMM DD YYYY'))
        }
    },
    formatNumber(val, precision = 1) {
        return (val && toFixed(val, precision)) || 0
    },
    reload() {
        this.showLoadingMask('')

        window.location.href = '/'
    },
    formatPercentage(value, precision = 0, addPercentageSymbol) {
        if (value || value === 0) {
            return (value * 100).toFixed(precision) + (addPercentageSymbol ? '%' : '')
        } else {
            return value
        }
    },
    formatScore(value, precision = 1, addPercentageSymbol) {
        if (value || value === 0) {
            return (value * 10).toFixed(precision)
        } else {
            return value
        }
    },
    openNewWindow(url) {
        if (url.indexOf('http://') < 0 && url.indexOf('https://') < 0) {
            url = 'http://' + url
        }

        window.open(url)
    },
    getVitrolifeSlideIdWithDescription(slide) {
        if (slide.esId && slide.esId.startsWith(slide.slideDescriptionId)) return slide.esId

        const slideEsId = this.extractVitrolifeSlideIdParts(slide.esId)
        if (slideEsId && slide.slideDescriptionId) {
            return `${slide.slideDescriptionId}-${slideEsId}`
        }

        return slideEsId
    },
    extractVitrolifeSlideIdParts(slideEsId) {
        if (!slideEsId) {
            return null
        }

        if (slideEsId.indexOf('*') > -1) {
            return slideEsId
        }

        const split = slideEsId.split('_')

        const parts = {
            slide: null,
            instrument: null
        }

        for (const part of split) {
            if (part.startsWith('S')) {
                parts.slide = part
            }
            if (part.startsWith('I')) {
                parts.instrument = part
            }
        }
        return parts.slide
    },

    flashElement(el, delay = 300, hideDelay = 300) {
        window.setTimeout(() => {
            el.classList.remove('un-flash')
            el.classList.add('flash')

            window.setTimeout(() => {
                el.classList.add('un-flash')
                el.classList.remove('flash')
            }, hideDelay)
        }, delay)
    },
    formatGardnerScore(value) {
        return (
            (value &&
                value.innerCellMass &&
                value.blastStage +
                    '' +
                    value.innerCellMass.toUpperCase() +
                    '' +
                    value.trophectoderm.toUpperCase()) ||
            ''
        )
    },
    formatHourSinceFertilization(...value) {
        if (value.length > 1) {
            const values = []

            for (const _value of value) {
                values.push(_value ? (_value * 1).toFixed(1) : '')
            }

            return values
        } else {
            return value ? (value * 1).toFixed(1) : ''
        }
    },
    async copyToClipboard(content, overrideSuccessMessage) {
        const $translate = Vue.$store.getters['locale/translate']

        try {
            await navigator.clipboard.writeText(content)

            this.toast($translate(overrideSuccessMessage || 'copied_to_clipboard'))
        } catch (err) {
            this.toast($translate('error_copying_to_clipboard'), 'error')
        }
    },
    formatPnHazardMessage(pnCount) {
        return Vue.$store.getters['locale/translate'](
            pnCount > 2 ? 'pn_count_poly_score_tooltip' : `pn_count_${pnCount}_score_tooltip`
        )
    },
    getPatientColor(createdAt) {
        const time = new Date(createdAt).getTime()

        const index = time.toString().replaceAll('0', '') % 100

        return patientColors[index]
    }
}

function  getScoreColor(
    tenant,
    score,
    lowThreshold,
    highThreshold,
    colorBackground,
    onlyBackgroundGreen,
    hexColors
) {
    let colorAttrType = 'text'

    if (hexColors) {
        colorAttrType = 'hex'
    } else if (colorBackground) {
        colorAttrType = 'background'
    }

    if (score == null) {
        return 'na_score'
    }

    score = UIUtils.formatScore(score)

    if (score <= lowThreshold) {
        return Theme.ragClasses[!onlyBackgroundGreen ? colorAttrType : 'text'].red
    } else if (score >= highThreshold) {
        return Theme.ragClasses[colorAttrType].green
    } else {
        return Theme.ragClasses[!onlyBackgroundGreen ? colorAttrType : 'text'].amber
    }
}

const patientColors = [
    '#78B8EC',
    '#E1D410',
    '#02F7C4',
    '#AF08FF',
    '#DF9098',
    '#5EEBFE',
    '#EEEEE6',
    '#CE36DD',
    '#394A66',
    '#BCE0E0',
    '#AF7680',
    '#CFDB10',
    '#12590E',
    '#3EFDD4',
    '#5B948B',
    '#CE7E17',
    '#A3556A',
    '#BBF390',
    '#45A0A7',
    '#EEFC53',
    '#211DE8',
    '#81ECC1',
    '#50997B',
    '#531356',
    '#623B30',
    '#F28AE4',
    '#BB4A90',
    '#41A08A',
    '#D15522',
    '#521520',
    '#531C41',
    '#C1568B',
    '#73CACC',
    '#9EF5B1',
    '#926410',
    '#122BAF',
    '#5728B6',
    '#B7D7C6',
    '#A5A33B',
    '#2C4999',
    '#B720C2',
    '#9DB602',
    '#90A78C',
    '#8CA2BD',
    '#B74CA7',
    '#44ECA2',
    '#0D58AA',
    '#362854',
    '#2AAF13',
    '#5D7F7B',
    '#849A18',
    '#07DC98',
    '#866990',
    '#0D5BC1',
    '#ABF305',
    '#346A0F',
    '#17DFEF',
    '#3074E0',
    '#051C22',
    '#AB73D1',
    '#C8E37A',
    '#67470C',
    '#F84F16',
    '#C7D633',
    '#20C79B',
    '#A3A642',
    '#FF8635',
    '#4EEC1D',
    '#3585F0',
    '#9F66EF',
    '#060DA3',
    '#FE438E',
    '#E14661',
    '#FF5C22',
    '#23493B',
    '#18ABD1',
    '#588DCD',
    '#406447',
    '#712D39',
    '#372618',
    '#BC5B99',
    '#D122F7',
    '#73B67A',
    '#AB1754',
    '#C6927C',
    '#BF84A9',
    '#4BF72C',
    '#37A74A',
    '#B6019D',
    '#D54788',
    '#374A7B',
    '#9FBB28',
    '#39095F',
    '#E6A6DD',
    '#6416EC',
    '#65260F',
    '#AB70D6',
    '#E071FE',
    '#051CEC',
    '#F1C3BE'
]
